import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

const NAVY  = '#071B3A';
const GOLD  = '#C99316';
const SLATE = '#536A80';
const IVORY = '#FAF8F3';

const BANNER_URL = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/jxiucnvi_Third%20culture%20leadership%20banner.webp';

const SIGNATURE_HTML = `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;max-width:520px;">
  <tr>
    <td style="padding-right:20px;border-right:2px solid rgba(201,147,22,0.4);vertical-align:top;">
      <img src="${BANNER_URL}" alt="Third Culture Leadership" height="48" style="height:48px;width:auto;display:block;margin-bottom:10px;" />
    </td>
    <td style="padding-left:20px;vertical-align:top;">
      <p style="margin:0 0 2px;font-size:15px;font-weight:700;color:#071B3A;line-height:1.2;">Dr Promise Monday</p>
      <p style="margin:0 0 10px;font-size:12px;color:#536A80;letter-spacing:0.06em;text-transform:uppercase;">Paediatrician &bull; Author &bull; Founder, Third Culture Leadership</p>
      <p style="margin:0 0 3px;font-size:12px;color:#536A80;">
        <a href="mailto:promise.monday@thirdcultureleadership.co.uk" style="color:#536A80;text-decoration:none;">promise.monday@thirdcultureleadership.co.uk</a>
      </p>
      <p style="margin:0 0 10px;font-size:12px;color:#536A80;">
        <a href="https://thirdcultureleadership.co.uk" style="color:#536A80;text-decoration:none;">thirdcultureleadership.co.uk</a>
      </p>
      <p style="margin:0;font-size:11px;font-style:italic;color:rgba(7,27,58,0.45);max-width:300px;line-height:1.5;">
        Cultural Fluency Without Compromise — Lead across cultures without losing who you are.
      </p>
    </td>
  </tr>
</table>`;

const EmailSignature = () => {
  const [copied, setCopied] = useState(false);
  const [htmlCopied, setHtmlCopied] = useState(false);

  const copyHTML = () => {
    navigator.clipboard.writeText(SIGNATURE_HTML).then(() => {
      setHtmlCopied(true);
      setTimeout(() => setHtmlCopied(false), 2000);
    });
  };

  const copyRendered = () => {
    // Create a temporary element to copy rendered HTML
    const el = document.createElement('div');
    el.innerHTML = SIGNATURE_HTML;
    document.body.appendChild(el);
    const range = document.createRange();
    range.selectNode(el);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);
    document.execCommand('copy');
    window.getSelection().removeAllRanges();
    document.body.removeChild(el);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen pt-24 pb-20" style={{ backgroundColor: IVORY }}>
      <div className="max-w-4xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="mb-14">
          <span
            className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-4"
            style={{ color: GOLD + 'CC' }}
          >
            Brand Asset
          </span>
          <h1
            className="font-serif font-light tracking-tight leading-[1.1] mb-4"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: NAVY }}
          >
            Email Signature
          </h1>
          <p className="font-sans font-light text-base" style={{ color: SLATE }}>
            Use this signature for all Third Culture Leadership correspondence — speaking enquiries, consulting, and professional email.
          </p>
        </div>

        {/* Signature Preview */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] font-sans font-medium mb-4" style={{ color: NAVY + '50' }}>
            Preview
          </p>
          <div className="bg-white p-8 border" style={{ borderColor: 'rgba(7,27,58,0.1)' }}>
            <div dangerouslySetInnerHTML={{ __html: SIGNATURE_HTML }} />
          </div>
        </div>

        {/* Copy buttons */}
        <div className="flex flex-wrap gap-4 mb-12">
          <button
            onClick={copyRendered}
            className="inline-flex items-center gap-2.5 px-6 py-3 font-sans text-sm uppercase tracking-[0.12em] font-medium transition-all duration-300"
            style={{ backgroundColor: NAVY, color: IVORY }}
            data-testid="copy-rendered-btn"
          >
            {copied ? <Check size={15} /> : <Copy size={15} />}
            {copied ? 'Copied!' : 'Copy Signature (for Gmail / Outlook)'}
          </button>
          <button
            onClick={copyHTML}
            className="inline-flex items-center gap-2.5 px-6 py-3 font-sans text-sm uppercase tracking-[0.12em] font-medium transition-all duration-300"
            style={{ border: '1px solid rgba(7,27,58,0.2)', color: NAVY + '80' }}
            data-testid="copy-html-btn"
          >
            {htmlCopied ? <Check size={15} /> : <Copy size={15} />}
            {htmlCopied ? 'Copied!' : 'Copy HTML Code'}
          </button>
        </div>

        {/* Instructions */}
        <div className="border-t pt-10" style={{ borderColor: 'rgba(7,27,58,0.08)' }}>
          <h2 className="font-serif font-light text-xl mb-6" style={{ color: NAVY }}>
            How to install
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                app: 'Gmail',
                steps: [
                  'Go to Settings → See all settings',
                  'Scroll to Signature section',
                  'Click "Create new" or edit existing',
                  'Paste the copied signature (use "Copy Signature" button above)',
                  'Save Changes',
                ],
              },
              {
                app: 'Outlook',
                steps: [
                  'Go to File → Options → Mail → Signatures',
                  'Click "New" to create a signature',
                  'In the editor, paste the copied signature',
                  'Select it as your default for new messages',
                  'Click OK',
                ],
              },
            ].map((item) => (
              <div key={item.app}>
                <h3
                  className="font-sans text-sm font-semibold uppercase tracking-[0.15em] mb-4"
                  style={{ color: GOLD }}
                >
                  {item.app}
                </h3>
                <ol className="space-y-2">
                  {item.steps.map((step, i) => (
                    <li key={i} className="flex gap-3 font-sans text-sm font-light" style={{ color: NAVY + '80' }}>
                      <span className="font-serif flex-shrink-0" style={{ color: GOLD + '80' }}>{i + 1}.</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>

        {/* HTML code block */}
        <div className="mt-12">
          <h2 className="font-serif font-light text-xl mb-4" style={{ color: NAVY }}>
            HTML Source
          </h2>
          <pre
            className="text-xs leading-relaxed overflow-x-auto p-6 rounded"
            style={{ backgroundColor: NAVY, color: IVORY + 'CC', fontFamily: 'monospace' }}
          >
            {SIGNATURE_HTML}
          </pre>
        </div>

      </div>
    </div>
  );
};

export default EmailSignature;
